import React from "react";
import { createShortLink } from "actions/shortLink";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik } from "formik";
import {
  prepareCreateShortLink,
  prepareCreateShortLinkInitialValues,
  prepareUnauthedCreateShortLink,
  validateCreateShortLinkForm
} from "utils/shortLink";
import {
  useAttachments,
  useCouponPromotionCodes,
  useCtxReferences,
  useIsAuthed,
  useProductsPrices,
  useShippingRates,
  useTaxRates
} from "utils/selectors";
import { usePrevPathFragment } from "utils/hooks/hub";
import { useRouter } from "next/router";
import { CELEBRATE } from "utils/constants/celebrate";
import ShortLinkBuilderContent from "components/ShortLinkBuilderContent";
import { SECURE_ACTIONS, verifyRequest } from "utils/security";

const ShortLinkBuilder = ({
  actions,
  requestParams = {},
  refreshResources,
  refreshing,
  afterCreate
}) => {
  const isAuthed = useIsAuthed();
  const backPath = usePrevPathFragment();

  const { products, prices } = useProductsPrices();
  const { coupons, promotionCodes } = useCouponPromotionCodes();
  const { replace, push, asPath } = useRouter();
  const taxRates = useTaxRates();
  const shippingRates = useShippingRates();
  const {
    application,
    merchantAccount,
    resourceID,
    resourceUUID,
    resource
  } = useCtxReferences();
  const attachments = useAttachments();

  const goShow = (shortId) => {
    push(`${asPath}/${shortId}?&celebrate=${CELEBRATE.BUILDER_PAYMENT_LINK}`);
  };

  const submitForm = (formValues, formikActions) => {
    const prepareFn = isAuthed
      ? prepareCreateShortLink
      : prepareUnauthedCreateShortLink;

    const data = prepareFn({
      values: formValues,
      products,
      prices,
      merchantAccount,
      coupons,
      promotionCodes,
      taxRates,
      attachments
    });
    const confirmCreateShortLink = () => {
      actions.createShortLink(
        {
          resourceID,
          resourceUUID,
          resource,
          data,
          ...requestParams
        },
        /**
         * Returns object with shortLink and metadata
         * @param {*} err
         * @param {Object<{shortLink: Object}>} responseData
         */
        (err, responseData) => {
          /**
           * 1. Turn off loader
           */
          formikActions.setSubmitting(false);
          /**
           * 2. Show errors or reset form
           */
          if (err) {
            formikActions.setErrors(err);
          } else {
            formikActions.resetForm({ values: formValues });
          }

          /**
           * 3a. Call afterCreate handler or
           * 3b. Perform default conditional navigation
           */
          if (afterCreate) {
            afterCreate(err, responseData);
          } else if (!err && responseData) {
            /**
             * If shortLink present then goShow
             * else replace with back path
             */
            responseData.shortLink && responseData.shortLink.short_id
              ? goShow(responseData.shortLink.short_id)
              : replace(backPath);
          }
        }
      );
    };

    if (isAuthed) {
      confirmCreateShortLink();
    } else {
      verifyRequest({
        action: SECURE_ACTIONS.PAYMENT_LINK,
        onSuccess: confirmCreateShortLink,
        onError: (err) => {
          throw err;
        }
      });
    }
  };

  const initialValues = prepareCreateShortLinkInitialValues({
    application,
    merchantAccount
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateCreateShortLinkForm({
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates
      })}
    >
      {() => (
        <ShortLinkBuilderContent
          showAuthedFields={isAuthed}
          refreshing={refreshing}
          refreshResources={refreshResources}
        />
      )}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createShortLink
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ShortLinkBuilder);
